import type { CSSProperties } from "react";

export type BoardRotDeg = 0 | 90 | 180 | 270;

export function normRot(raw: number): BoardRotDeg {
  const r = ((Math.round(raw / 90) * 90) % 360 + 360) % 360;
  if (r === 90 || r === 180 || r === 270) return r;
  return 0;
}

/** Sans advance ≈ em (Cyrillic a bit wider). Names use 600 weight. */
const CHAR_EM = 0.72;
/** Medium booth-code badge — tighter than bold 800 so 4 chars fit. */
const CODE_CHAR_EM = 0.58;
/** Corner badge may use at most this fraction of card width. */
const CODE_MAX_W_FRAC = 0.55;

function wrapLineCount(text: string, charsPerLine: number): number {
  const limit = Math.max(1, Math.floor(charsPerLine));
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 1;
  let lines = 1;
  let cur = 0;
  for (const w of words) {
    const wl = w.length;
    if (cur === 0) {
      if (wl > limit) {
        lines += Math.ceil(wl / limit) - 1;
        cur = wl % limit || limit;
      } else {
        cur = wl;
      }
      continue;
    }
    if (cur + 1 + wl <= limit) {
      cur += 1 + wl;
    } else {
      lines += 1;
      if (wl > limit) {
        lines += Math.ceil(wl / limit) - 1;
        cur = wl % limit || limit;
      } else {
        cur = wl;
      }
    }
  }
  return Math.max(1, lines);
}

/**
 * Auto-fit sizes in board-local CSS px (viewport scale is separate).
 * Name uses the FULL card — booth code is an overlay and must not shrink the name box.
 * `labelScale` multiplies auto-fit size (0.4–2.5). Below 1 shrinks; above 1 enlarges
 * (overflow is clipped by the marker box).
 */
export function fitPinLabel(opts: {
  wPct: number;
  hPct: number;
  boardW: number;
  boardH: number;
  viewZoom?: number;
  labelScale?: number;
  nameLines?: number;
  /** Extra rotation on the name only (not board). */
  nameRot?: number;
  boothCode?: string;
  nameText?: string;
}): { textPx: number; codePx: number; pad: number } {
  const shrink = Math.min(2.5, Math.max(0.4, opts.labelScale ?? 1));
  const cardW = (Math.max(0.5, opts.wPct) / 100) * Math.max(1, opts.boardW);
  const cardH = (Math.max(0.5, opts.hPct) / 100) * Math.max(1, opts.boardH);
  const pad = Math.max(2.5, Math.min(6, Math.min(cardW, cardH) * 0.08));

  const booth = (opts.boothCode ?? "").trim();
  const boothChars = Math.max(2, booth.length || 4);
  // Size to the badge's real max width (not full card) so "A311" never becomes "A…".
  const codeBadgeMaxW = Math.max(8, cardW * CODE_MAX_W_FRAC - 4);
  const codePx = Math.max(
    4.5,
    Math.min(
      7.5,
      codeBadgeMaxW / (boothChars * CODE_CHAR_EM),
      cardH * 0.12,
      Math.min(cardW, cardH) * 0.14,
    ),
  );

  const name = (opts.nameText ?? "").trim();
  const maxLines = opts.nameLines === 1 ? 1 : opts.nameLines === 3 ? 3 : opts.nameLines === 4 ? 4 : 2;
  const nameRot = normRot(opts.nameRot ?? 0);
  const swapped = nameRot === 90 || nameRot === 270;
  let areaW = Math.max(4, cardW - pad * 2);
  let areaH = Math.max(4, cardH - pad * 2);
  if (swapped) {
    const t = areaW;
    areaW = areaH;
    areaH = t;
  }

  let textPx = 5;
  if (name) {
    let lo = 4;
    // Base auto-fit cap; labelScale > 1 may grow past this (overflow clipped by pin).
    let hi = Math.min(18, areaH / 1.15, areaW / (2.2 * CHAR_EM));
    for (let i = 0; i < 14; i++) {
      const mid = (lo + hi) / 2;
      const cpl = areaW / (mid * CHAR_EM);
      const need = wrapLineCount(name, cpl);
      if (need <= maxLines && need * mid * 1.25 <= areaH) lo = mid;
      else hi = mid;
    }
    // Apply scale after fit so 50% / 250% always changes size (was capped at 15px).
    textPx = Math.max(4, Math.min(36, lo * shrink));
  }

  return {
    textPx: Math.round(textPx * 10) / 10,
    codePx: Math.round(codePx * 10) / 10,
    pad: Math.round(pad * 10) / 10,
  };
}

/**
 * Pin-local corner that appears as the visual top-left after the board's CSS rotate(CW).
 * No counter-rotate on the text — stays inside overflow:hidden.
 */
export function visualTopLeftCorner(
  boardRot: number,
  insetPx: number,
): CSSProperties {
  const r = normRot(boardRot);
  const inset = Math.max(2, insetPx);
  if (r === 90) return { top: "auto", right: "auto", bottom: inset, left: inset };
  if (r === 180) return { top: "auto", left: "auto", bottom: inset, right: inset };
  if (r === 270) return { bottom: "auto", left: "auto", top: inset, right: inset };
  return { bottom: "auto", right: "auto", top: inset, left: inset };
}

export function longestTokenLen(text: string): number {
  const parts = text.split(/[\s·|/;,]+/).filter(Boolean);
  if (!parts.length) return Math.max(4, text.length);
  return Math.max(...parts.map((p) => p.length), 4);
}
